import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Tabs,
  Tab,
  Grid,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import type { Worker } from '../../types/worker';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../lib/supabase';
import PhoneIcon from '@mui/icons-material/Phone';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import type { ChipProps } from '@mui/material';
import { addWorker } from '../../services/workerService';

interface ManageWorkersDialogProps {
  open: boolean;
  onClose: () => void;
  farmId: string;
  onChange: () => void;
  currency?: string;
  initialTab?: number;
}

const defaultPermissions = {
  owner: [
    'manage_workers',
    'manage_tasks',
    'view_analytics',
    'manage_settings',
    'manage_finances',
  ],
  manager: [
    'manage_tasks',
    'view_analytics',
    'view_finances',
  ],
  worker: [
    'view_tasks',
    'update_tasks',
  ],
  viewer: [
    'view_tasks',
  ],
};

const supportedCurrencies = [
  { code: 'KSH', label: 'Kenyan Shilling (KSH)' },
  { code: 'USD', label: 'US Dollar (USD)' },
  { code: 'EUR', label: 'Euro (EUR)' },
  { code: 'GBP', label: 'British Pound (GBP)' },
  { code: 'KES', label: 'Kenyan Shilling (KES)' },
  { code: 'NGN', label: 'Nigerian Naira (NGN)' },
  { code: 'ZAR', label: 'South African Rand (ZAR)' },
  // Add more as needed
];

const leaveStatusOptions = [
  'pending', 'approved', 'rejected', 'cancelled'
];

const editableFields = [
  'name', 'role', 'wage', 'paymentType', 'permissions', 'leaveDays', 'leaveHistory', 'phoneNumber', 'email', 'status', 'specialization', 'workingHours', 'emergencyContact', 'notes'
];

const specializationGroups = [
  {
    group: 'Crop Management',
    color: 'success',
    specializations: ['Planting', 'Irrigation', 'Pest Control', 'Harvesting'],
  },
  {
    group: 'Animal Care',
    color: 'warning',
    specializations: ['Feeding', 'Milking', 'Health', 'Breeding'],
  },
  {
    group: 'Equipment',
    color: 'info',
    specializations: ['Tractor Operation', 'Maintenance', 'Irrigation System'],
  },
  {
    group: 'Farm Management',
    color: 'primary',
    specializations: ['Record Keeping', 'Supervision', 'Training'],
  },
  {
    group: 'Other',
    color: 'default',
    specializations: ['Other'],
  },
];

type LeaveRequestStatus = 'pending' | 'approved' | 'rejected';

const ManageWorkersDialog: React.FC<ManageWorkersDialogProps> = ({
  open,
  onClose,
  farmId,
  onChange,
  currency = 'KSH',
  initialTab = 0,
}) => {
  const [currentTab, setCurrentTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [editingWorker, setEditingWorker] = useState<Worker | null>(null);
  const [newWorker, setNewWorker] = useState<Partial<Worker>>({
    name: '',
    role: 'worker',
    phoneNumber: '',
    email: '',
    startDate: '',
    wage: 0,
    paymentType: 'monthly',
    status: 'active',
    specialization: [],
    workingHours: { start: '08:00', end: '17:00' },
    leaveDays: 0,
    leaveHistory: [],
    emergencyContact: { name: '', relationship: '', phone: '' },
    notes: '',
    permissions: [],
  });
  const [workerType, setWorkerType] = useState<'digital' | 'non-digital'>('non-digital');
  const [selectedCurrency, setSelectedCurrency] = useState(currency);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useEffect(() => {
    if (open) setCurrentTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) return;
    const fetchWorkers = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase
          .from('workers')
          .select('*')
          .eq('farm_id', farmId);
        if (error) throw error;
        // Map snake_case to camelCase for UI
        const mapped = (data || []).map(w => ({
          ...w,
          phoneNumber: w.phone_number,
          paymentType: w.payment_type,
          // add more mappings as needed
        }));
        setWorkers(mapped);
      } catch (err: any) {
        setError('Failed to fetch workers.');
        setWorkers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkers();
  }, [open, farmId]);

  const handleNewWorkerChange = (field: keyof Worker, value: any) => {
    setNewWorker(prev => ({ ...prev, [field]: value }));
  };

  const handleAddWorker = async () => {
    setLoading(true);
    setError(null);
    try {
      // Only send fields that are filled in
      const workerToAdd: any = {};
      Object.entries(newWorker).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          workerToAdd[key] = value;
        }
      });
      workerToAdd.farmId = farmId;
      const newWorkerData = await addWorker(workerToAdd);
      setWorkers(prev => [...prev, newWorkerData]);
      setNewWorker({
        name: '',
        role: 'worker',
        phoneNumber: '',
        email: '',
        startDate: '',
        wage: 0,
        paymentType: 'monthly',
        status: 'active',
        specialization: [],
        workingHours: { start: '08:00', end: '17:00' },
        leaveDays: 0,
        leaveHistory: [],
        emergencyContact: { name: '', relationship: '', phone: '' },
        notes: '',
        permissions: [],
      });
      onChange && onChange();
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to add worker.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditWorker = (worker: Worker) => {
    setEditingWorker(worker);
  };

  const handleSaveEdit = async () => {
    if (!editingWorker || !farmId) {
      setError('No farm selected.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Only include editable fields in the update payload
      const updateData: any = {};
      editableFields.forEach(field => {
        const value = (editingWorker as Record<string, any>)[field];
        if (value !== undefined) {
          if (field === 'phoneNumber') updateData['phone_number'] = value;
          else if (field === 'paymentType') updateData['payment_type'] = value;
          else if (field === 'leaveDays') updateData['leave_days'] = value;
          else if (field === 'leaveHistory') updateData['leave_history'] = value;
          else if (field === 'workingHours') updateData['working_hours'] = value;
          else if (field === 'emergencyContact') updateData['emergency_contact'] = value;
          else updateData[field] = value;
        }
      });
      updateData['farm_id'] = farmId;
      const { error: updateError } = await supabase
        .from('workers')
        .update(updateData)
        .eq('id', editingWorker.id);
      if (updateError) throw updateError;
      setEditingWorker(null);
      onChange && onChange();
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update worker.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorker = async (workerId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { error: deleteError } = await supabase
        .from('workers')
        .delete()
        .eq('id', workerId);
      if (deleteError) throw deleteError;
      onChange();
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete worker.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = () => {
    if (onChange) onChange();
    onClose();
  };

  // Add a handler to update currency in farm settings
  const handleCurrencyChange = async (e: any) => {
    const newCurrency = e.target.value;
    setSelectedCurrency(newCurrency);
    // Save to farm settings in Supabase
    if (farmId) {
      await supabase
        .from('farms')
        .update({ currency: newCurrency })
        .eq('id', farmId);
      // Optionally, call onChange to refresh parent
      onChange && onChange();
    }
  };

  // Handle avatar file change
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ position: 'relative' }}>
        Manage Farm Workers
        <Box sx={{ position: 'absolute', top: 16, right: 24, width: 160, zIndex: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Currency</InputLabel>
            <Select
              value={selectedCurrency}
              label="Currency"
              onChange={handleCurrencyChange}
            >
              {supportedCurrencies.map((cur) => (
                <MenuItem key={cur.code} value={cur.code}>{cur.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Workers List" />
          <Tab label="Add New Worker" />
        </Tabs>

        {currentTab === 0 && (
          <Box>
            <List>
              {(workers || []).map((worker) => (
                <React.Fragment key={worker.id}>
                  <ListItem>
                    {editingWorker?.id === worker.id ? (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Name"
                            value={editingWorker.name}
                            onChange={(e) => setEditingWorker({
                              ...editingWorker,
                              name: e.target.value,
                            })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                              value={editingWorker.role as Worker['role']}
                              label="Role"
                              onChange={(e) => setEditingWorker({
                                ...editingWorker,
                                role: e.target.value as Worker['role'],
                                permissions: defaultPermissions[e.target.value as keyof typeof defaultPermissions],
                              })}
                            >
                              <MenuItem value="owner">Owner</MenuItem>
                              <MenuItem value="manager">Manager</MenuItem>
                              <MenuItem value="worker">Worker</MenuItem>
                              <MenuItem value="viewer">Viewer</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Wage"
                            value={editingWorker.wage}
                            onChange={(e) => setEditingWorker({
                              ...editingWorker,
                              wage: Number(e.target.value),
                            })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth>
                            <InputLabel>Payment Type</InputLabel>
                            <Select
                              value={editingWorker.paymentType}
                              label="Payment Type"
                              onChange={(e) => setEditingWorker({
                                ...editingWorker,
                                paymentType: e.target.value as 'daily' | 'weekly' | 'monthly',
                              })}
                            >
                              <MenuItem value="daily">Daily</MenuItem>
                              <MenuItem value="weekly">Weekly</MenuItem>
                              <MenuItem value="monthly">Monthly</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    ) : (
                      <>
                        <ListItemText
                          primary={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">{worker.name}</Typography>
                              {worker.phoneNumber && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                  <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    {worker.phoneNumber}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <MonetizationOnIcon fontSize="small" sx={{ mr: 0.5, color: 'success.main' }} />
                                <Typography variant="body2" component="span">
                                  Salary: {selectedCurrency} {worker.wage} / {worker.paymentType}
                                </Typography>
                              </Box>
                              <Typography variant="body2" component="div">
                                {worker.role.charAt(0).toUpperCase() + worker.role.slice(1)}
                                {worker.email && (
                                  <>
                                    {' '}
                                    <Chip
                                      label="Invited"
                                      color="info"
                                      size="small"
                                      sx={{ ml: 1 }}
                                    />
                                  </>
                                )}
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                {(worker.permissions || []).map((permission) => (
                                  <Chip
                                    key={permission}
                                    label={permission.replace('_', ' ')}
                                    size="small"
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleEditWorker(worker)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleDeleteWorker(worker.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </>
                    )}
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {currentTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {/* Avatar Upload */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={avatarPreview || undefined} sx={{ width: 56, height: 56 }} />
                  <Button variant="outlined" component="label">
                    Upload Avatar
                    <input type="file" accept="image/*" hidden onChange={handleAvatarChange} />
                  </Button>
                </Box>
              </Grid>
              {/* Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={newWorker.name}
                  onChange={e => handleNewWorkerChange('name', e.target.value)}
                  required
                />
              </Grid>
              {/* Role Dropdown */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={newWorker.role}
                    label="Role"
                    onChange={e => handleNewWorkerChange('role', e.target.value)}
                  >
                    <MenuItem value="owner">Owner</MenuItem>
                    <MenuItem value="manager">Manager</MenuItem>
                    <MenuItem value="worker">Worker</MenuItem>
                    <MenuItem value="viewer">Viewer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Specialization Multi-Select Dropdown */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Specialization</InputLabel>
                  <Select
                    multiple
                    value={newWorker.specialization || []}
                    label="Specialization"
                    onChange={e => handleNewWorkerChange('specialization', e.target.value)}
                    renderValue={(selected) => Array.isArray(selected) ? selected.join(', ') : ''}
                  >
                    {['Livestock', 'Crop', 'Machinery', 'Logistics', 'Veterinary', 'Irrigation', 'Security', 'Cleaning', 'Other'].map(option => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={newWorker.specialization?.includes(option) || false} />
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Phone Number */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={newWorker.phoneNumber}
                  onChange={e => handleNewWorkerChange('phoneNumber', e.target.value)}
                  required
                />
              </Grid>
              {/* Email */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newWorker.email}
                  onChange={e => handleNewWorkerChange('email', e.target.value)}
                  required
                  error={!!newWorker.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newWorker.email)}
                  helperText={!!newWorker.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newWorker.email) ? 'Invalid email' : ''}
                />
              </Grid>
              {/* Start Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={newWorker.startDate}
                  onChange={e => handleNewWorkerChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              {/* Wage */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Wage"
                  value={newWorker.wage}
                  onChange={e => handleNewWorkerChange('wage', Number(e.target.value))}
                  required
                  inputProps={{ min: 0 }}
                  error={!!newWorker.wage && newWorker.wage <= 0}
                  helperText={!!newWorker.wage && newWorker.wage <= 0 ? 'Wage must be greater than 0' : ''}
                />
              </Grid>
              {/* Payment Type */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Payment Type</InputLabel>
                  <Select
                    value={newWorker.paymentType}
                    label="Payment Type"
                    onChange={e => handleNewWorkerChange('paymentType', e.target.value)}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Status */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newWorker.status}
                    label="Status"
                    onChange={e => handleNewWorkerChange('status', e.target.value)}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="on-leave">On Leave</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Working Hours Start */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Working Hours Start"
                  value={newWorker.workingHours?.start || ''}
                  onChange={e => handleNewWorkerChange('workingHours', { ...newWorker.workingHours, start: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              {/* Working Hours End */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Working Hours End"
                  value={newWorker.workingHours?.end || ''}
                  onChange={e => handleNewWorkerChange('workingHours', { ...newWorker.workingHours, end: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              {/* Leave Days */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Leave Days"
                  value={newWorker.leaveDays}
                  onChange={e => handleNewWorkerChange('leaveDays', Number(e.target.value))}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              {/* Emergency Contact Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Name"
                  value={newWorker.emergencyContact?.name || ''}
                  onChange={e => handleNewWorkerChange('emergencyContact', {
                    name: e.target.value,
                    relationship: newWorker.emergencyContact?.relationship || '',
                    phone: newWorker.emergencyContact?.phone || ''
                  })}
                />
              </Grid>
              {/* Emergency Contact Relationship */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Relationship"
                  value={newWorker.emergencyContact?.relationship || ''}
                  onChange={e => handleNewWorkerChange('emergencyContact', {
                    name: newWorker.emergencyContact?.name || '',
                    relationship: e.target.value,
                    phone: newWorker.emergencyContact?.phone || ''
                  })}
                />
              </Grid>
              {/* Emergency Contact Phone */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Phone"
                  value={newWorker.emergencyContact?.phone || ''}
                  onChange={e => handleNewWorkerChange('emergencyContact', {
                    name: newWorker.emergencyContact?.name || '',
                    relationship: newWorker.emergencyContact?.relationship || '',
                    phone: e.target.value
                  })}
                />
              </Grid>
              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes"
                  value={newWorker.notes}
                  onChange={e => handleNewWorkerChange('notes', e.target.value)}
                />
              </Grid>
              {/* Add Worker Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddWorker}
                  disabled={
                    !newWorker.name ||
                    !newWorker.role ||
                    !newWorker.email ||
                    !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newWorker.email || '') ||
                    !newWorker.phoneNumber ||
                    !newWorker.startDate ||
                    !newWorker.wage || newWorker.wage <= 0
                  }
                >
                  Add Worker
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {editingWorker ? (
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Save Changes
          </Button>
        ) : (
          <Button
            onClick={handleSaveAll}
            variant="contained"
            color="primary"
          >
            Save All
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ManageWorkersDialog; 